// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { z } from 'zod';
import {
  INVERSIS_ACTIVITY_OPTIONS,
  INVERSIS_SECTOR_OPTIONS,
} from './inversis-id-map';

export enum EmploymentStatus {
  Employed = 'employed',
  SelfEmployed = 'self-employed',
  NotWorking = 'not-working',
}

export const employedProfileSchema = z
  .object({
    companyName: z.string().min(1, 'Company name is required'),
    sector: z.string().min(1, 'Sector is required'),
    companyActivity: z.string().min(1, 'Company activity is required'),
    employmentStatus: z.literal(EmploymentStatus.Employed),
  })
  .check(({ value, issues }) => {
    const data = value;

    if (!!data.sector && !INVERSIS_SECTOR_OPTIONS.has(data.sector)) {
      issues.push({
        code: 'custom',
        input: data.sector,
        path: ['sector'],
        message: 'Invalid sector ID',
      });
    }

    if (
      !!data.companyActivity &&
      !INVERSIS_ACTIVITY_OPTIONS.has(data.companyActivity)
    ) {
      issues.push({
        code: 'custom',
        input: data.companyActivity,
        path: ['companyActivity'],
        message: 'Invalid company activity ID',
      });
    }
  });

export const selfEmployedProfileSchema = z
  .object({
    businessName: z.string().min(1, 'Business name is required'),
    sector: z.string().min(1, 'Sector is required'),
    mainActivity: z.string().min(1, 'Main activity is required'),
    employmentStatus: z.literal(EmploymentStatus.SelfEmployed),
  })
  .check(({ value, issues }) => {
    const data = value;

    if (!!data.sector && !INVERSIS_SECTOR_OPTIONS.has(data.sector)) {
      issues.push({
        code: 'custom',
        input: data.sector,
        path: ['sector'],
        message: 'Invalid sector ID',
      });
    }

    if (!!data.mainActivity && !INVERSIS_ACTIVITY_OPTIONS.has(data.mainActivity)) {
      issues.push({
        code: 'custom',
        input: data.mainActivity,
        path: ['mainActivity'],
        message: 'Invalid main activity ID',
      });
    }
  });

export const notWorkingProfileSchema = z.object({
  employmentStatus: z.literal(EmploymentStatus.NotWorking),
});

export const professionalProfileSchema = z.discriminatedUnion('employmentStatus', [
  employedProfileSchema,
  selfEmployedProfileSchema,
  notWorkingProfileSchema,
]);

export type EmployedProfileFormData = z.infer<typeof employedProfileSchema>;

export type SelfEmployedProfileFormData = z.infer<typeof selfEmployedProfileSchema>;

export type NotWorkingProfileFormData = z.infer<typeof notWorkingProfileSchema>;

export type ProfessionalProfileFormData = z.infer<typeof professionalProfileSchema>;
